import { CARE_SERVICE_BASE_URL } from "constants";
import apiClient from "utils/apiClient";

const BASE = `${CARE_SERVICE_BASE_URL}/api/v1`;

const unwrap = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data)) return payload.data;
  if (payload?.data !== undefined) return payload.data;
  if (payload?.result !== undefined) return payload.result;
  return payload;
};

const list = (path, params) => apiClient.get(`${BASE}${path}`, { params }).then(unwrap);
const get = (path) => apiClient.get(`${BASE}${path}`).then(unwrap);
const post = (path, data) => apiClient.post(`${BASE}${path}`, data).then(unwrap);
const put = (path, data) => apiClient.put(`${BASE}${path}`, data).then(unwrap);
const remove = (path) => apiClient.delete(`${BASE}${path}`).then(unwrap);
const requiredId = (value, label) => {
  if (value === undefined || value === null || value === "") {
    throw new Error(`${label} is required.`);
  }
  return encodeURIComponent(value);
};

export const careService = {
  dashboard: (params) => get(`/dashboard${params?.workspaceId ? `?workspaceId=${encodeURIComponent(params.workspaceId)}` : ""}`),

  residents: {
    list: (params) => list("/residents", params),
    get: (residentId) => get(`/residents/${requiredId(residentId, "Resident id")}`),
    create: (data) => post("/residents", data),
    register: (data) => post("/residents/registrations", data),
    createOnboardingBio: (data) => post("/residents/onboarding/bio", data),
    updateOnboardingBio: (residentId, data) => put(`/residents/${requiredId(residentId, "Resident id")}/onboarding/bio`, data),
    saveOnboardingContacts: (residentId, data) => put(`/residents/${requiredId(residentId, "Resident id")}/onboarding/contacts`, data),
    saveOnboardingCareHistory: (residentId, data) => put(`/residents/${requiredId(residentId, "Resident id")}/onboarding/care-history`, data),
    saveOnboardingCareSnapshot: (residentId, data) => put(`/residents/${requiredId(residentId, "Resident id")}/onboarding/care-snapshot`, data),
    saveOnboardingDocuments: (residentId, data) => put(`/residents/${requiredId(residentId, "Resident id")}/onboarding/documents`, data),
    getOnboardingPreview: (residentId) => get(`/residents/${requiredId(residentId, "Resident id")}/onboarding/preview`),
    confirmOnboarding: (residentId, data) => post(`/residents/${requiredId(residentId, "Resident id")}/onboarding/confirm`, data),
    listRegistrations: (params) => list("/residents/registrations", params),
    getRegistration: (registrationId) => get(`/residents/registrations/${encodeURIComponent(registrationId)}`),
    update: (residentId, data) => put(`/residents/${requiredId(residentId, "Resident id")}`, data),
    delete: (residentId) => remove(`/residents/${requiredId(residentId, "Resident id")}`),
  },

  assessmentProcesses: {
    list: (params) => list("/assessment-processes", params),
    get: (assessmentProcessId) => get(`/residents/ncp-workflows/${encodeURIComponent(assessmentProcessId)}`),
    create: (data) => post("/assessment-processes", data),
    update: (assessmentProcessId, data) => put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}`, data),
    delete: (assessmentProcessId) => remove(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}`),
  },

  assessmentRecords: {
    list: (params) => list("/assessment-records", params),
    get: (recordId) => get(`/assessment-records/${encodeURIComponent(recordId)}`),
    byProcessAndType: (assessmentProcessId, recordType) =>
      get(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}`),
    saveForProcess: (assessmentProcessId, recordType, data) =>
      put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/records/${encodeURIComponent(recordType)}`, data),
  },

  negotiatedCarePlans: {
    list: (params) => list("/negotiated-care-plans", params),
    get: (planId) => get(`/negotiated-care-plans/${encodeURIComponent(planId)}`),
    saveForProcess: (assessmentProcessId, data) =>
      put(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan`, data),
    generate: (assessmentProcessId, data) =>
      post(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/generate`, data),
    refine: (assessmentProcessId, data) =>
      post(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/refine`, data),
    format: (assessmentProcessId, data) =>
      post(`/assessment-processes/${encodeURIComponent(assessmentProcessId)}/negotiated-care-plan/format`, data),
  },

  dailyNotes: {
    list: (params) => list("/daily-notes", params),
    create: (data) => post("/daily-notes", data),
    update: (noteId, data) => put(`/daily-notes/${encodeURIComponent(noteId)}`, data),
    delete: (noteId) => remove(`/daily-notes/${encodeURIComponent(noteId)}`),
  },

  careStaffAssignments: {
    list: (params) => list("/care-staff-assignments", params),
    create: (data) => post("/care-staff-assignments", data),
    update: (assignmentId, data) => put(`/care-staff-assignments/${encodeURIComponent(assignmentId)}`, data),
    delete: (assignmentId) => remove(`/care-staff-assignments/${encodeURIComponent(assignmentId)}`),
  },

  documents: {
    list: (params) => list("/documents", params),
    get: (documentId) => get(`/documents/${encodeURIComponent(documentId)}`),
    create: (data) => post("/documents", data),
    delete: (documentId) => remove(`/documents/${encodeURIComponent(documentId)}`),
  },
};

export default careService;
